import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {searchHeader} from 'src/app.component.module.styl';
import {HeaderFilter} from 'src/components/header-filter';
import {FILTER_WIDTH} from 'src/constants';
import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {COLUMNS, COLUMN_LABELS, MAX_INPUT_LENGTH, MIN_NAME_LEN, TABLE_COLUMN_WIDTHS} from 'src/modules/vendor-admin/constants';
import {getDisplayStatus} from 'src/modules/vendor-admin/service/util';
import {
    ApprovalRequestSearchCriteriaSortOrder,
} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';
import {RequestStatus} from 'src/openapi/admin/models/RequestStatus';
import {formatTimestamp} from 'src/utils/date-utils';
import {isVendorAdminRole} from 'src/utils/role-utils';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,
    type HeaderCellOptions,
    type RowData,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Input} from '@pb/kit-component-input-v2';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {SortHeaderWithDataTable} from '@pb/kit-component-sort-header';


import {rowNumberHeader} from './requests-table.module.styl';

import type {ApprovalRequestChangelogItem} from 'src/openapi/admin/models/ApprovalRequestChangelogItem';
import type {ApprovalRequestSearchCriteria} from 'src/openapi/admin/models/ApprovalRequestSearchCriteria';
import type {ApprovalRequestSearchItem} from 'src/openapi/admin/models/ApprovalRequestSearchItem';

const getResolvedBy = (item: ApprovalRequestChangelogItem | undefined): string => {
    if (!item) {
        return '';
    }
    const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
    return name || item.userEmail || '';
};

type Props = {
    requests: ApprovalRequestSearchItem[],
    criteria: ApprovalRequestSearchCriteria,
    onUpdateCriteria: (criteria: Partial<ApprovalRequestSearchCriteria>) => void,
    enableStatusFilter?: boolean,
    showStatusColumn?: boolean,
    hasActiveFilter: boolean,
    noResultsMessage: string,
    actionColumn?: (request: ApprovalRequestSearchItem) => React.ReactNode,
    showReasonColumn?: boolean,
    totalCount: number,
    changelog?: Record<string, ApprovalRequestChangelogItem>,
    showApprovedByColumns?: boolean,
    showDeniedByColumns?: boolean,
};

export const RequestsTable: React.FC<Props> = ({
    requests,
    criteria,
    onUpdateCriteria,
    enableStatusFilter = true,
    showStatusColumn = false,
    hasActiveFilter,
    noResultsMessage,
    actionColumn,
    showReasonColumn = false,
    totalCount,
    changelog,
    showApprovedByColumns = false,
    showDeniedByColumns = false,
}) => {
    const {currentUser} = useCurrentUser();
    const isVendorAdmin = isVendorAdminRole(currentUser?.role?.id);
    const showVendorColumn = !isVendorAdmin;

    const [firstNameValue, setFirstNameValue] = useState(criteria.firstName ?? '');
    const [lastNameValue, setLastNameValue] = useState(criteria.lastName ?? '');
    const [statusValue, setStatusValue] = useState<RequestStatus | undefined>(criteria.requestStatuses?.[0]);

    useEffect(() => {
        setFirstNameValue(criteria.firstName ?? '');
    }, [criteria.firstName]);
    useEffect(() => {
        setLastNameValue(criteria.lastName ?? '');
    }, [criteria.lastName]);
    useEffect(() => {
        setStatusValue(criteria.requestStatuses?.[0]);
    }, [criteria.requestStatuses]);

    const handleStatusApply = useCallback(() => {
        onUpdateCriteria({requestStatuses: statusValue ? [statusValue] : [], page: 1});
    }, [onUpdateCriteria, statusValue]);

    const handleStatusReset = useCallback(() => {
        setStatusValue(undefined);
        onUpdateCriteria({requestStatuses: [], page: 1});
    }, [onUpdateCriteria]);

    const handleFirstNameReset = useCallback(() => {
        setFirstNameValue('');
        onUpdateCriteria({firstName: undefined, page: 1});
    }, [onUpdateCriteria]);

    const handleFirstNameApply = useCallback(() => {
        const trimmed = firstNameValue.trim();
        onUpdateCriteria({firstName: trimmed.length >= MIN_NAME_LEN ? trimmed : undefined, page: 1});
    }, [firstNameValue, onUpdateCriteria]);

    const handleFirstNameCancel = useCallback(() => {
        setFirstNameValue(criteria.firstName ?? '');
    }, [criteria.firstName]);

    const handleLastNameReset = useCallback(() => {
        setLastNameValue('');
        onUpdateCriteria({lastName: undefined, page: 1});
    }, [onUpdateCriteria]);

    const handleLastNameApply = useCallback(() => {
        const trimmed = lastNameValue.trim();
        onUpdateCriteria({lastName: trimmed.length >= MIN_NAME_LEN ? trimmed : undefined, page: 1});
    }, [lastNameValue, onUpdateCriteria]);

    const handleLastNameCancel = useCallback(() => {
        setLastNameValue(criteria.lastName ?? '');
    }, [criteria.lastName]);

    const requestDateSortDirection = criteria.sortOrder === ApprovalRequestSearchCriteriaSortOrder.ASC
        ? 'ASC'
        : 'DESC';

    const columns = useMemo((): Column[] => {
        const cols: Column[] = [
            {columnId: COLUMNS.ROW_NUMBER, fixed: true, defaultWidth: TABLE_COLUMN_WIDTHS.INDEX},
        ];
        if (showStatusColumn) {
            cols.push({columnId: COLUMNS.STATUS, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.STATUS});
        }
        cols.push(
            {columnId: COLUMNS.EMAIL, fixed: true, defaultWidth: TABLE_COLUMN_WIDTHS.EMAIL},
            {columnId: COLUMNS.PREFERRED_EMAIL, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.PREFERRED_EMAIL},
            {columnId: COLUMNS.PARTNER_EMAIL, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.PARTNER_EMAIL},
            {columnId: COLUMNS.FIRST_NAME, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.FIRST_NAME},
            {columnId: COLUMNS.LAST_NAME, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.LAST_NAME},
        );
        if (showVendorColumn) {
            cols.push({columnId: COLUMNS.VENDOR_NAME, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.VENDOR_NAME});
        }
        cols.push({columnId: COLUMNS.RULE, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.RULE_NAME});
        if (showReasonColumn) {
            cols.push({columnId: COLUMNS.REASON, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.REASON});
        }
        cols.push(
            {columnId: COLUMNS.REQUEST_DATE, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.REQUEST_DATES},
            {columnId: COLUMNS.LOCATION, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.LOCATION},
        );
        if (showApprovedByColumns) {
            cols.push(
                {columnId: COLUMNS.APPROVED_BY, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.RESOLVED_BY},
                {columnId: COLUMNS.APPROVE_DATE, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.RESOLVED_DATE},
            );
        }
        if (showDeniedByColumns) {
            cols.push(
                {columnId: COLUMNS.DENIED_BY, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.RESOLVED_BY},
                {columnId: COLUMNS.DENIED_DATE, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.RESOLVED_DATE},
            );
        }
        if (actionColumn) {
            cols.push({columnId: COLUMNS.ACTION, fixed: false, defaultWidth: TABLE_COLUMN_WIDTHS.ACTION});
        }
        return cols;
    }, [
        showStatusColumn, showVendorColumn, showReasonColumn,
        showApprovedByColumns, showDeniedByColumns, actionColumn,
    ]);

    const rows = useMemo((): RowData[] =>
        requests.map((request, index) => ({
            id: request.requestId,
            columnValues: {
                [COLUMNS.ROW_NUMBER]: (criteria.page - 1) * criteria.pageSize + index + 1,
                [COLUMNS.STATUS]: getDisplayStatus(request.requestStatus),
                [COLUMNS.EMAIL]: request.requestUserInfo.email ?? '',
                [COLUMNS.PREFERRED_EMAIL]: request.preferredEmail ?? '',
                [COLUMNS.PARTNER_EMAIL]: request.partnerEmail ?? '',
                [COLUMNS.FIRST_NAME]: request.requestUserInfo.firstName ?? '',
                [COLUMNS.LAST_NAME]: request.requestUserInfo.lastName ?? '',
                [COLUMNS.VENDOR_NAME]: request.vendorInfo.vendorName ?? '',
                [COLUMNS.RULE]: request.ruleName ?? '',
                [COLUMNS.REASON]: request.reason ?? '',
                [COLUMNS.REQUEST_DATE]: formatTimestamp(request.requestDate),
                [COLUMNS.LOCATION]: request.location ?? '',
                [COLUMNS.APPROVED_BY]: getResolvedBy(changelog?.[request.requestId]),
                [COLUMNS.APPROVE_DATE]: changelog?.[request.requestId]?.timestamp
                    ? formatTimestamp(changelog[request.requestId].timestamp)
                    : '',
                [COLUMNS.DENIED_BY]: getResolvedBy(changelog?.[request.requestId]),
                [COLUMNS.DENIED_DATE]: changelog?.[request.requestId]?.timestamp
                    ? formatTimestamp(changelog[request.requestId].timestamp)
                    : '',
            },
        })),
    [requests, criteria.page, criteria.pageSize, changelog],
    );

    const tableMessage = hasActiveFilter ? 'Try adjusting the search criteria.' : noResultsMessage;

    return (
        <DataTable
            data={{rows, columns}}
            headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                const wrapper = (children: React.ReactNode) => (
                    <Cell
                        {...cellOptions}
                        key={column.columnId}
                        column={column}
                        resizable={false}>
                        <TableHeader indent="none" className={searchHeader}>
                            {children}
                        </TableHeader>
                    </Cell>
                );

                switch (column.columnId) {
                    case COLUMNS.STATUS:
                        return wrapper(
                            enableStatusFilter ? (
                                <HeaderFilter
                                    caption="Status"
                                    filterActive={
                                        criteria.requestStatuses?.length ? true : undefined
                                    }
                                    sortable={false}
                                    direction={undefined}
                                    defaultDirection="ASC"
                                    onSort={() => {}}
                                    onReset={handleStatusReset}
                                    onApply={handleStatusApply}
                                    onCancel={() => setStatusValue(criteria.requestStatuses?.[0])}>
                                    <ComboBox<string, string>
                                        value={statusValue ?? ''}
                                        onChange={val => {
                                            const v = Array.isArray(val) ? val[0] : val;
                                            setStatusValue(v ? v as RequestStatus : undefined);
                                        }}
                                        placeholder="All statuses"
                                        style={{minWidth: 160}}>
                                        <ComboboxItem caption="All statuses" value=""/>
                                        {Object.values(RequestStatus).map(status => (
                                            <ComboboxItem
                                                key={status}
                                                value={status}
                                                caption={getDisplayStatus(status)}
                                            />
                                        ))}
                                    </ComboBox>
                                </HeaderFilter>
                            ) : (
                                COLUMN_LABELS[COLUMNS.STATUS]
                            ),
                        );
                    case COLUMNS.FIRST_NAME:
                        return wrapper(
                            <HeaderFilter
                                caption="First Name"
                                filterActive={criteria.firstName ? true : undefined}
                                onReset={handleFirstNameReset}
                                onApply={handleFirstNameApply}
                                onCancel={handleFirstNameCancel}
                                contentWidth={FILTER_WIDTH}>
                                <Input
                                    id="request-first-name-filter"
                                    value={firstNameValue}
                                    placeholder="First name (min. 2 characters)"
                                    maxLength={MAX_INPUT_LENGTH}
                                    onInput={e => setFirstNameValue(
                                        (e as React.FormEvent<HTMLInputElement>).currentTarget.value,
                                    )}
                                />
                            </HeaderFilter>,
                        );
                    case COLUMNS.LAST_NAME:
                        return wrapper(
                            <HeaderFilter
                                caption="Last Name"
                                filterActive={criteria.lastName ? true : undefined}
                                onReset={handleLastNameReset}
                                onApply={handleLastNameApply}
                                onCancel={handleLastNameCancel}
                                contentWidth={FILTER_WIDTH}>
                                <Input
                                    id="request-last-name-filter"
                                    value={lastNameValue}
                                    placeholder="Last name (min. 2 characters)"
                                    maxLength={MAX_INPUT_LENGTH}
                                    onInput={e => setLastNameValue(
                                        (e as React.FormEvent<HTMLInputElement>).currentTarget.value,
                                    )}
                                />
                            </HeaderFilter>,
                        );
                    case COLUMNS.REQUEST_DATE:
                        return wrapper(
                            <SortHeaderWithDataTable
                                caption="Request Date"
                                defaultDirection="DESC"
                                sortable={true}
                                showFilter={false}
                                direction={requestDateSortDirection}
                                onSort={(dir: string) => {
                                    onUpdateCriteria({
                                        sortOrder: dir === 'ASC'
                                            ? ApprovalRequestSearchCriteriaSortOrder.ASC
                                            : ApprovalRequestSearchCriteriaSortOrder.DESC,
                                        page: 1,
                                    });
                                }}
                            />,
                        );
                    case COLUMNS.ROW_NUMBER:
                        return wrapper(
                            <div className={rowNumberHeader}>
                                {COLUMN_LABELS[COLUMNS.ROW_NUMBER]}
                            </div>,
                        );
                    default:
                        return wrapper(COLUMN_LABELS[column.columnId]);
                }
            }}
            renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                const {rowIndex, column: {columnId}} = rowsParams;
                const value = rowsParams.row.columnValues[columnId];
                const column = rowsParams.column;
                const cellKey = rowIndex + columnId;

                const wrapper = (children: React.ReactNode) => (
                    <Cell
                        {...cellOptions}
                        key={cellKey}
                        column={column}
                        className="search-result-cell">
                        {children}
                    </Cell>
                );

                switch (columnId) {
                    case COLUMNS.ROW_NUMBER:
                        return wrapper(value as React.ReactNode);
                    case COLUMNS.ACTION: {
                        const request = requests.find(r => r.requestId === String(rowsParams.row.id));
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}>
                                {request && actionColumn ? actionColumn(request) : null}
                            </Cell>
                        );
                    }
                    default:
                        return wrapper(
                            <EllipsisTextHint title={String(value ?? '')}>
                                {String(value ?? '')}
                            </EllipsisTextHint>,
                        );
                }
            }}
            paginationStripe={(
                <PaginationStripe
                    goToPage={true}
                    pageSize={criteria.pageSize}
                    totalCount={totalCount}
                    currentPage={criteria.page}
                    onPaginationChange={({currentPage, pageSize}) => {
                        onUpdateCriteria({page: currentPage, pageSize});
                        window.scrollTo(0, 0);
                    }}
                    sizeSwitcherPosition="LEFT"
                    sizeSwitcherType="ONLY_PAGE_SIZE"
                    sizes={[10, 25, 50, 100, 250]}
                />
            )}
            showNoDataMsg={!rows.length}
            tableMessage={tableMessage}
        />
    );
};
